import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactionAPI, beneficiaryAPI, departmentAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, Wallet, ArrowRight, ArrowLeft, Loader2, CheckCircle, AlertTriangle, Building2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInitiatePayment = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [departments, setDepartments] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filteredBeneficiaries, setFilteredBeneficiaries] = useState([]);
  
  const [formData, setFormData] = useState({
    department_id: '',
    beneficiary_id: '',
    amount: '',
    description: '',
    reference_number: ''
  });

  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedBenef, setSelectedBenef] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, benefRes] = await Promise.all([
        departmentAPI.list(),
        beneficiaryAPI.listAll ? beneficiaryAPI.listAll() : beneficiaryAPI.list()
      ]);
      setDepartments(deptRes.data);
      setBeneficiaries(benefRes.data.filter(b => !b.is_blacklisted));
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeptChange = (deptId) => {
    setFormData({ ...formData, department_id: deptId, beneficiary_id: '' });
    const dept = departments.find(d => d.department_id === deptId);
    setSelectedDept(dept);
    setSelectedBenef(null);
    
    // Filter beneficiaries by department
    const filtered = beneficiaries.filter(b => b.department_id === deptId);
    setFilteredBeneficiaries(filtered);
  };

  const handleBenefChange = (benefId) => {
    setFormData({ ...formData, beneficiary_id: benefId });
    const benef = filteredBeneficiaries.find(b => b.beneficiary_id === benefId);
    setSelectedBenef(benef);
  };

  const handleNext = () => {
    if (!formData.department_id) {
      toast.error('Please select a department');
      return;
    }
    if (!formData.beneficiary_id) {
      toast.error('Please select a beneficiary');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description) {
      toast.error('Please enter a description');
      return;
    }
    
    const amount = parseFloat(formData.amount);
    if (amount > (selectedDept?.balance || 0)) {
      toast.error('Insufficient department balance');
      return;
    }
    
    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Admin can specify department in request
      const res = await fetch(`${API_URL}/api/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          department_id: formData.department_id,
          beneficiary_id: formData.beneficiary_id,
          amount: parseFloat(formData.amount),
          description: formData.description,
          reference_number: formData.reference_number || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'COMPLETED') {
          toast.success('Payment auto-approved and completed!');
        } else {
          toast.success('Payment submitted for approval!');
        }
        navigate('/admin/transactions');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit payment');
      }
    } catch (err) {
      toast.error('Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const isAutoApproval = selectedDept?.auto_approval_threshold && 
    formData.amount && 
    parseFloat(formData.amount) <= selectedDept.auto_approval_threshold;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="admin-initiate-payment-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Initiate Payment</h1>
          <p className="text-gray-500 mt-1">Send payments on behalf of any department</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
          <div className={`w-20 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
        </div>

        {/* Step 1: Payment Details */}
        {step === 1 && (
          <Card className="glass-card animate-fadeInUp">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="text-lg text-gray-900">Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Department Selection */}
              <div className="space-y-2">
                <Label className="text-gray-600">Department</Label>
                <Select value={formData.department_id} onValueChange={handleDeptChange}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="department-select">
                    <SelectValue placeholder="Select a department" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-300">
                    {departments.map(d => (
                      <SelectItem key={d.department_id} value={d.department_id} className="text-gray-900">
                        <div className="flex items-center justify-between w-full">
                          <span>{d.name}</span>
                          <span className="text-gray-500 text-sm ml-2">({formatCurrency(d.balance)})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department Info */}
              {selectedDept && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900">{selectedDept.name}</p>
                          <p className="text-sm text-gray-500">Balance: {formatCurrency(selectedDept.balance)}</p>
                        </div>
                      </div>
                      {selectedDept.auto_approval_threshold && (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <Zap className="w-3 h-3 mr-1" />
                          Auto-approve ≤ {formatCurrency(selectedDept.auto_approval_threshold)}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Beneficiary Selection */}
              <div className="space-y-2">
                <Label className="text-gray-600">Beneficiary</Label>
                <Select 
                  value={formData.beneficiary_id} 
                  onValueChange={handleBenefChange}
                  disabled={!formData.department_id}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="beneficiary-select">
                    <SelectValue placeholder={formData.department_id ? "Select a beneficiary" : "Select department first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-300">
                    {filteredBeneficiaries.length > 0 ? (
                      filteredBeneficiaries.map(b => (
                        <SelectItem key={b.beneficiary_id} value={b.beneficiary_id} className="text-gray-900">
                          {b.name} - {b.upi_id || b.account_number}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled className="text-gray-500">
                        No beneficiaries in this department
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="text-gray-600">Amount (₹)</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  className="bg-white border-gray-300 text-gray-900 font-['JetBrains_Mono'] text-lg h-12"
                  data-testid="amount-input"
                />
                {formData.amount && parseFloat(formData.amount) > (selectedDept?.balance || 0) && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Exceeds department balance
                  </p>
                )}
                {isAutoApproval && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Zap className="w-4 h-4" /> Will be auto-approved (below threshold)
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-gray-600">Description / Purpose</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Payment for services..."
                  className="bg-white border-gray-300 text-gray-900 min-h-20"
                  data-testid="description-input"
                />
              </div>

              {/* Reference Number */}
              <div className="space-y-2">
                <Label className="text-gray-600">Reference Number (Optional)</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                  placeholder="INV-001"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              <Button
                onClick={handleNext}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white mt-4"
                disabled={!formData.department_id || filteredBeneficiaries.length === 0}
                data-testid="next-btn"
              >
                Continue <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Review & Confirm */}
        {step === 2 && (
          <Card className="glass-card animate-fadeInUp">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="text-lg text-gray-900">Review & Confirm</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Department</span>
                  <span className="text-gray-900 font-medium">{selectedDept?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Beneficiary</span>
                  <span className="text-gray-900 font-medium">{selectedBenef?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Account Details</span>
                  <span className="text-gray-900 font-['JetBrains_Mono'] text-sm">{selectedBenef?.upi_id || selectedBenef?.account_number}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Amount</span>
                  <span className="text-gray-900 font-['JetBrains_Mono'] text-xl">{formatCurrency(parseFloat(formData.amount))}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Description</span>
                  <span className="text-gray-900 text-right max-w-xs">{formData.description}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-gray-500">Dept Balance After</span>
                  <span className="text-blue-600 font-['JetBrains_Mono']">
                    {formatCurrency((selectedDept?.balance || 0) - parseFloat(formData.amount))}
                  </span>
                </div>
              </div>

              {isAutoApproval ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-700 text-sm flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    This payment will be <strong>auto-approved</strong> immediately (amount is below department threshold).
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-700 text-sm">
                    Note: As an admin, this payment will still go to the approval queue for audit compliance.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-gray-300 text-gray-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={submitting}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</>
                  ) : isAutoApproval ? (
                    <><CheckCircle className="w-5 h-5 mr-2" /> Approve & Send</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Submit for Approval</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminInitiatePayment;
